import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Reveal } from "@/components/Motion";
import { Search, Plus, MapPin } from "lucide-react";

const BUDGETS = [2000, 5000, 8000, 10000, 15000, 20000, 30000, 50000];
const Field = ({ label, children }) => (
  <label className="flex-1 min-w-0 px-4 py-2.5 lg:border-r border-navy/10 last:border-0">
    <span className="block text-[11px] uppercase tracking-widest font-semibold text-slatev">{label}</span>
    {children}
  </label>
);

export function SearchPanel({ makes = [] }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ make: "", max_price: "", postcode: "", verified: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const go = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (f.make) p.set("make", f.make);
    if (f.max_price) p.set("max_price", f.max_price);
    if (f.postcode) { p.set("postcode", f.postcode); p.set("radius", "50"); }
    if (f.verified) p.set("verified", "1");
    navigate(`/browse?${p.toString()}`);
  };
  const trig = "border-0 shadow-none p-0 h-8 bg-transparent font-medium text-navy focus:ring-0";

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-24 lg:pt-32">
      <Reveal><h2 className="font-heading font-extrabold text-navy tracking-tighter text-4xl sm:text-5xl lg:text-6xl">A good car.<br />A fair deal.</h2></Reveal>
      <Reveal delay={0.1}>
        <form onSubmit={go} data-testid="home-search-form" className="mt-10 bg-white rounded-2xl lg:rounded-full border border-navy/10 shadow-[0_20px_50px_-30px_rgba(22,36,47,0.4)] p-2 flex flex-col lg:flex-row lg:items-center">
          <Field label="Make">
            <Select value={f.make || "any"} onValueChange={(v) => set("make", v === "any" ? "" : v)}>
              <SelectTrigger className={trig} data-testid="search-make"><SelectValue placeholder="Any make" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="any">Any make</SelectItem>{makes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Budget up to">
            <Select value={f.max_price || "any"} onValueChange={(v) => set("max_price", v === "any" ? "" : v)}>
              <SelectTrigger className={trig} data-testid="search-budget"><SelectValue placeholder="No limit" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="any">No limit</SelectItem>{BUDGETS.map((b) => <SelectItem key={b} value={String(b)}>£{b.toLocaleString()}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Location">
            <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slatev" />
              <Input value={f.postcode} onChange={(e) => set("postcode", e.target.value.toUpperCase())} placeholder="Your postcode" data-testid="search-location" className="border-0 shadow-none p-0 h-8 focus-visible:ring-0 font-medium" /></div>
          </Field>
          <button type="submit" data-testid="search-submit" className="m-1 h-14 px-8 rounded-xl lg:rounded-full bg-teal hover:bg-teal-dark text-white font-semibold inline-flex items-center justify-center gap-2 transition-[transform,background-color] hover:-translate-y-0.5">
            <Search className="w-4 h-4" /> Find a car
          </button>
        </form>
      </Reveal>
      <Reveal delay={0.2} className="mt-5 flex items-center gap-6 px-2">
        <Link to="/browse" data-testid="search-all-filters" className="inline-flex items-center gap-1 text-sm font-semibold text-navy hover:text-teal transition-colors">All filters <Plus className="w-4 h-4" /></Link>
        <label className="inline-flex items-center gap-2 text-sm text-navy cursor-pointer">
          <Checkbox checked={f.verified} onCheckedChange={(v) => set("verified", !!v)} data-testid="search-verified" className="border-navy/30 data-[state=checked]:bg-teal data-[state=checked]:border-teal" />
          Verified only
        </label>
      </Reveal>
    </section>
  );
}
